// ─── AutoChinBot · Engine 2 — "etask" ──────────────────────────
// Task engine. Consumes the "edata" dataset and runs the AutoChinBot tasks:
//
//   task1 — Big-candle (trend candle) detector
//     When a candle closes, compute the A0 body = |close - open| in pips.
//     If body >= X pips (X configurable PER SYMBOL), flag it as a trend candle
//     and emit an event. (The Telegram conversation for it is designed in Part 4.)
//
//   task3 — ST50 convergence detector (NEW)
//     When a candle closes, compute the distance (in pips) between SMA50 and
//     TMA50 at A0. If the distance is <= X pips (X configurable PER SYMBOL,
//     default 5), flag it as an ST50 convergence and emit an event so a
//     conversation with the user can start. (Case designed in Part 4.)
//
//   task4 — TMA50 cross detector (NEW)
//     When a candle closes, check whether the latest closed candle crossed the
//     TMA50 line relative to the previous candle:
//       • cross UP   → close(A1) <= TMA50(A1) AND close(A0) >= TMA50(A0)
//       • cross DOWN → close(A1) >= TMA50(A1) AND close(A0) <= TMA50(A0)
//     On a detected cross, emit an event so a conversation with the user can
//     start. (Case designed in Part 4.) No threshold — always active.
//
//   task2 — Global min/max levels
//     When a candle closes, compute the min & max CLOSE over the last
//     50 / 100 / 200 completed candles and store them as global variables
//     (overwritten on every close) + persist to `autochin_etask_levels`.
//
// The engine is pure detection: it emits typed `AutoChinTaskEvent`s and never
// sends anything itself. The runtime layer decides what to do with an event.

import type { AutoChinEdataEngine, EdataCandleCloseEvent } from '@/lib/autochin-edata-engine';
import { persistEtaskLevels, type AutoChinMinMaxLevels } from '@/lib/autochin-etask-store';

// ─── Types ──────────────────────────────────────────────────────

export interface AutoChinEtaskConfig {
  /** task1: minimum A0 body (in pips) to count as a trend / big candle. */
  bigCandlePips?: number;
  /** task3: maximum |SMA50 − TMA50| distance (in pips) to flag ST50 convergence. Default 5. */
  st50Pips?: number;
}

export type AutoChinTaskEventType =
  | 'task1_big_candle'
  | 'task3_st50_convergence'
  | 'task4_tma50_cross'
  | 'task5_risk_block';

export interface AutoChinTaskEvent {
  id: string;
  type: AutoChinTaskEventType;
  symbolId: number;
  symbolName: string;
  at: number;
  /** Human-readable one-liner (used by the UI + a basic Telegram notification). */
  message: string;
  /** task1 fields. */
  bodyPips?: number;
  direction?: 'BULL' | 'BEAR';
  /** task1 fields — the configured body threshold (pips) that was applied. */
  thresholdPips?: number;
  /** task3 fields. */
  st50Pips?: number;
  /** task4 fields. */
  crossDir?: 'UP' | 'DOWN';
  /** task5 fields — which risk rule blocked the order. */
  blockedBy?: string;
  /** task5 fields — human-readable reason for the block. */
  riskReason?: string;
}

// ─── Engine ─────────────────────────────────────────────────────

export class AutoChinEtaskEngine {
  private edata: AutoChinEdataEngine;
  private userId: string;

  private configs: Record<number, AutoChinEtaskConfig> = {};
  private levels: Record<number, AutoChinMinMaxLevels> = {};
  /** task1 dedup: symbolId → A0 timestamp already handled. */
  private task1Done: Record<number, number> = {};
  /** task3 dedup: symbolId → A0 timestamp already handled. */
  private task3Done: Record<number, number> = {};
  /** task4 dedup: symbolId → A0 timestamp already handled. */
  private task4Done: Record<number, number> = {};

  private eventListeners = new Set<(e: AutoChinTaskEvent) => void>();
  private unsubEdata: (() => void) | null = null;
  private started = false;

  private dirty = new Set<number>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(edata: AutoChinEdataEngine, userId: string) {
    this.edata = edata;
    this.userId = userId;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.unsubEdata = this.edata.onCandleClose((e) => this.onCandleClose(e));
  }

  stop(): void {
    this.started = false;
    if (this.unsubEdata) { this.unsubEdata(); this.unsubEdata = null; }
    if (this.flushTimer) { clearTimeout(this.flushTimer); this.flushTimer = null; }
  }

  applyConfigs(configs: Record<number, AutoChinEtaskConfig>): void {
    this.configs = { ...configs };
  }

  onEvent(fn: (e: AutoChinTaskEvent) => void): () => void {
    this.eventListeners.add(fn);
    return () => { this.eventListeners.delete(fn); };
  }

  getLevels(symbolId: number): AutoChinMinMaxLevels | undefined {
    return this.levels[symbolId];
  }

  // ─── task1 + task3 + task2 (on candle close) ─────────────────

  private onCandleClose(e: EdataCandleCloseEvent): void {
    const cfg = this.configs[e.symbolId] || {};

    // task2 — always recompute the global levels on every close (incl. init).
    this.updateLevels(e);

    // task1 / task3 / task4 — detection tasks. Skip the initial dataset
    // (avoid notifying on load).
    if (e.initial) return;
    this.checkBigCandle(e, cfg);
    this.checkSt50(e, cfg);
    this.checkTma50(e);
  }

  // ─── task1 — trend / big candle ──────────────────────────────

  private checkBigCandle(e: EdataCandleCloseEvent, cfg: AutoChinEtaskConfig): void {
    const threshold = cfg.bigCandlePips;
    if (!threshold || threshold <= 0) return;

    const a0 = e.edata.candles[0];
    if (!a0) return;
    if (this.task1Done[e.symbolId] === a0.timestamp) return; // already handled

    const bodyPips = Math.abs(a0.close - a0.open) / e.edata.pipSize;
    if (bodyPips < threshold) return;

    this.task1Done[e.symbolId] = a0.timestamp;
    const direction: 'BULL' | 'BEAR' = a0.close >= a0.open ? 'BULL' : 'BEAR';

    this.emit({
      id: `task1:${e.symbolId}:${a0.timestamp}`,
      type: 'task1_big_candle',
      symbolId: e.symbolId,
      symbolName: e.symbolName,
      at: Date.now(),
      bodyPips,
      direction,
      thresholdPips: threshold,
      message:
        `🔥 Nến xu hướng ${e.symbolName} — thân ${bodyPips.toFixed(1)} pips ` +
        `(ngưỡng ${threshold}p) · ${direction}`,
    });
  }

  // ─── task3 — ST50 convergence (SMA50 ↔ TMA50) ────────────────

  private checkSt50(e: EdataCandleCloseEvent, cfg: AutoChinEtaskConfig): void {
    // Default threshold is 5 pips when the user has not configured one.
    const threshold = cfg.st50Pips ?? 5;
    if (threshold <= 0) return; // 0 or negative disables the task

    const a0 = e.edata.candles[0];
    if (!a0) return;
    if (this.task3Done[e.symbolId] === a0.timestamp) return; // already handled

    const sma50 = e.edata.indicators.sma50[0];
    const tma50 = e.edata.indicators.tma50[0];
    // Not enough candles yet → indicator is null → nothing to detect.
    if (sma50 == null || tma50 == null) return;

    const gapPips = Math.abs(sma50 - tma50) / e.edata.pipSize;
    if (gapPips > threshold) return;

    this.task3Done[e.symbolId] = a0.timestamp;

    this.emit({
      id: `task3:${e.symbolId}:${a0.timestamp}`,
      type: 'task3_st50_convergence',
      symbolId: e.symbolId,
      symbolName: e.symbolName,
      at: Date.now(),
      st50Pips: gapPips,
      message:
        `🎯 ST50 hội tụ ${e.symbolName} — SMA50 ↔ TMA50 cách ${gapPips.toFixed(1)} pips ` +
        `(ngưỡng ≤ ${threshold}p)`,
    });
  }

  // ─── task4 — TMA50 cross detector ────────────────────────────

  private checkTma50(e: EdataCandleCloseEvent): void {
    // The edata arrays are newest-first, so index 0 === A0 and index 1 === A1.
    const a0 = e.edata.candles[0];
    const a1 = e.edata.candles[1];
    if (!a0 || !a1) return; // need at least 2 closed candles
    if (this.task4Done[e.symbolId] === a0.timestamp) return; // already handled

    const tma0 = e.edata.indicators.tma50[0];
    const tma1 = e.edata.indicators.tma50[1];
    // Not enough candles yet → indicator is null → nothing to detect.
    if (tma0 == null || tma1 == null) return;

    // cross UP   → close(A1) <= TMA50(A1) AND close(A0) >= TMA50(A0)
    // cross DOWN → close(A1) >= TMA50(A1) AND close(A0) <= TMA50(A0)
    const crossedUp = a1.close <= tma1 && a0.close >= tma0;
    const crossedDown = a1.close >= tma1 && a0.close <= tma0;
    if (!crossedUp && !crossedDown) return;

    this.task4Done[e.symbolId] = a0.timestamp;

    // If both hold (A1 exactly on the line), prefer the A0 side of the line.
    const crossDir: 'UP' | 'DOWN' = crossedUp && !crossedDown
      ? 'UP'
      : crossedDown && !crossedUp
        ? 'DOWN'
        : a0.close >= tma0 ? 'UP' : 'DOWN';

    this.emit({
      id: `task4:${e.symbolId}:${a0.timestamp}`,
      type: 'task4_tma50_cross',
      symbolId: e.symbolId,
      symbolName: e.symbolName,
      at: Date.now(),
      crossDir,
      message:
        `✂️ Nến cắt TMA50 ${e.symbolName} — A0 đóng ${a0.close} ${crossDir === 'UP' ? '≥' : '≤'} ` +
        `TMA50 ${tma0} · ${crossDir === 'UP' ? 'cắt lên' : 'cắt xuống'}`,
    });
  }

  // ─── task2 — compute + persist min/max levels ────────────────

  private updateLevels(e: EdataCandleCloseEvent): void {
    const candles = e.edata.candles;

    const minMaxOver = (n: number): { min: number | null; max: number | null } => {
      const slice = candles.slice(0, Math.min(n, candles.length));
      if (slice.length === 0) return { min: null, max: null };
      let min = Infinity;
      let max = -Infinity;
      for (const c of slice) {
        if (c.close < min) min = c.close;
        if (c.close > max) max = c.close;
      }
      return { min, max };
    };

    const l50 = minMaxOver(50);
    const l100 = minMaxOver(100);
    const l200 = minMaxOver(200);

    this.levels[e.symbolId] = {
      min50: l50.min, max50: l50.max,
      min100: l100.min, max100: l100.max,
      min200: l200.min, max200: l200.max,
      candleTimestamp: candles[0]?.timestamp ?? null,
      updatedAt: Date.now(),
    };

    this.dirty.add(e.symbolId);
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flushLevels();
    }, 1200);
  }

  private flushLevels(): void {
    if (this.dirty.size === 0) return;
    const rows = [...this.dirty]
      .map((id) => ({
        symbolId: id,
        symbolName: this.edata.get(id)?.symbolName || `#${id}`,
        levels: this.levels[id],
      }))
      .filter((r) => !!r.levels);
    this.dirty.clear();
    if (rows.length > 0) persistEtaskLevels(this.userId, rows);
  }

  // ─── Emit ────────────────────────────────────────────────────

  private emit(e: AutoChinTaskEvent): void {
    this.eventListeners.forEach((fn) => {
      try { fn(e); } catch { /* ignore listener errors */ }
    });
  }
}